// v0.5.0 — `ReasonPicker` — preset reasons for the 5 audit-text fields
// signed off in round C. A2-571 (operator 2026-10-27): NO free text — the
// picked preset is the note (shown read-only), its blanks are picked; the
// textarea and the "Other (explain)" option are gone.
// (TypedInEditor, SignoffPanel, GroupedReviewQueue,
// ViesHeldQueue, OffenderActions).
//
// Behaviour:
//   - Vertical column of chip buttons above a textarea.
//   - Clicking a non-freeText chip fills the textarea with the
//     chip's `template`. Operator can edit before saving.
//   - The `Other (explain)` chip just focuses the textarea
//     empty; no template applied.
//   - Selected chip stays ringed (teal) so the operator + any
//     auditor can see which preset they started from.
//   - Three-tier char counter (slate / amber / emerald) — same
//     pattern v0.4.99 shipped on TypedInEditor.
//   - Placeholder validation (mode `a` — fail loud, signed off
//     round C): if the trimmed text still contains any
//     `<TOKEN>` placeholder, `isValid` is `false` AND the
//     remaining tokens are listed on the counter line so the
//     parent's Save button can disable + the operator can see
//     exactly what's left.
//
// Public surface:
//   <ReasonPicker
//     value={string}
//     onChange={(string) => void}
//     onValidityChange={(boolean) => void}     // optional
//     presets={ReasonPreset[]}
//     minChars={20}
//     exampleAnswer={string}                   // optional caption
//     testid={string}                          // root + textarea
//     placeholder={string}                     // optional textarea ghost
//     country={string}                         // optional — enables RateTokenFiller
//     onDate={string}                          // optional — YYYY-MM-DD, required if country set
//     regime={string}                          // optional — EU_OSS (default) | UK_VAT
//     tokenAutofill={ "<AMOUNT>": "457.00 EUR", "<ROW_COUNT>": "3" }
//                                              // optional — substitutes tokens at preset-pick time
//   />
//
// `ReasonPreset` shape:
//   { id, label, template? (string), freeText? (boolean) }
import React, { useState } from "react";
import { remainingPlaceholders, describePlaceholders, TOKEN_LABELS } from "./reasonPresets";
import { RateTokenFiller } from "./RateTokenFiller";
import { EU_GB_CHOICES, countryName, countryOptionLabel } from "../lib/countries";
import { useTranslation } from "react-i18next";

export const computeValidity = (text, minChars) => {
  const trimmed = (text || "").trim();
  const remaining = remainingPlaceholders(trimmed);
  return {
    lengthOk: trimmed.length >= minChars,
    placeholdersOk: remaining.length === 0,
    remaining,
    trimmedLen: trimmed.length,
  };
};

// Operator check 2026-10-16: a template with a blank (e.g. <DOC_REF>) kept
// Save disabled until the operator found the token inside the text and typed
// over it — nobody would guess that. Each blank gets its own labelled field
// that fills the text. Rate blanks keep RateTokenFiller.
const RATE_TOKENS = new Set(["<RATE>", "<RATE_DESC>"]);

// A2-430 (operator 2026-10-19: "avoid free typing anywhere within the app
// completely"): a date blank is PICKED from a calendar, never typed, and
// never pre-set to today — the date names a past event (the accountant
// supplying a figure, a customer confirming a number), so a default of
// today would be a false claim the operator might not notice.
const DateBlankFiller = ({ token, value, onChange, testid, label }) => {
  const { t } = useTranslation();
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div data-testid={testid} className="flex items-center gap-2 text-xs">
      <label className="flex-1 flex flex-col gap-1">
        <span className="text-amber-700 dark:text-amber-300">{t("ui.reason_picker.blank_pick", { label, defaultValue: `Pick ${label}:` })}</span>
        <input
          type="date"
          max={today}
          data-testid={`${testid}-date`}
          onChange={(e) => {
            const d = e.target.value;
            if (d) onChange((value || "").replace(token, d));
          }}
          className="w-full rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1 text-base sm:text-xs"
        />
      </label>
    </div>
  );
};

// A2-430 step 2 — blanks with a finite answer are PICKED, not typed.
// Names, descriptions, references and web addresses stay typed until the
// picker design for them is agreed (6.3c).
const CHOICES = {
  "<COUNTRY>": EU_GB_CHOICES,
  "<ROLE>": ["the client", "the client's accountant", "the client's director",
             "the marketplace", "the customer"],
  "<MARKETPLACE>": ["Amazon", "eBay", "Etsy", "Shopify", "Zalando", "Cdiscount",
                    "Allegro", "bol.com", "Kaufland", "OnBuy", "Fnac"],
  "<ADAPTER>": ["the client template", "the marketplace report"],
};
const NUMBER_TOKENS = new Set(["<ROW_COUNT>", "<SAMPLE_COUNT>", "<AMOUNT>"]);

const ChoiceBlankFiller = ({ token, value, onChange, testid, label }) => {
  const { t } = useTranslation();
  const opts = CHOICES[token];
  const show = (o) => (token === "<COUNTRY>" ? countryOptionLabel(o) : o);
  const text = (o) => (token === "<COUNTRY>" ? (countryName(o) || o) : o);
  return (
    <label data-testid={testid} className="flex flex-col gap-1 text-xs">
      <span className="text-amber-700 dark:text-amber-300">{t("ui.reason_picker.blank_pick", { label, defaultValue: `Pick ${label}:` })}</span>
      <select defaultValue="" data-testid={`${testid}-select`}
              onChange={(e) => { if (e.target.value) onChange((value || "").replace(token, text(e.target.value))); }}
              className="w-full rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 px-2 py-1 text-base sm:text-xs">
        <option value="" disabled>{t("ui.reason_picker.choose", { defaultValue: "Choose…" })}</option>
        {opts.map((o) => <option key={o} value={o}>{show(o)}</option>)}
      </select>
    </label>
  );
};

const DateRangeFiller = ({ token, value, onChange, testid, label }) => {
  const { t } = useTranslation();
  const [from, setFrom] = useState("");
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div data-testid={testid} className="flex flex-col gap-1 text-xs">
      <span className="text-amber-700 dark:text-amber-300">{t("ui.reason_picker.blank_pick", { label, defaultValue: `Pick ${label}:` })}</span>
      <div className="flex items-center gap-2">
        <input type="date" max={today} aria-label={t("ui.reason_picker.from", { defaultValue: "From" })}
               data-testid={`${testid}-from`} onChange={(e) => setFrom(e.target.value)}
               className="rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1 text-base sm:text-xs" />
        <span>–</span>
        <input type="date" min={from || undefined} max={today} disabled={!from}
               aria-label={t("ui.reason_picker.to", { defaultValue: "To" })} data-testid={`${testid}-to`}
               onChange={(e) => { if (from && e.target.value) onChange((value || "").replace(token, `${from} to ${e.target.value}`)); }}
               className="rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1 text-base sm:text-xs disabled:opacity-50" />
      </div>
    </div>
  );
};

const BlankFiller = ({ token, value, onChange, testid }) => {
  const { t } = useTranslation();
  const [answer, setAnswer] = useState("");
  const fill = () => {
    const a = answer.trim();
    if (!a) return;
    onChange((value || "").replace(token, a));
    setAnswer("");
  };
  const label = TOKEN_LABELS[token] || "the missing detail";
  return (
    <div data-testid={testid} className="flex items-center gap-2 text-xs">
      <label className="flex-1 flex flex-col gap-1">
        <span className="text-amber-700 dark:text-amber-300">{t("ui.reason_picker.blank_fill", { label, defaultValue: `Fill in ${label}:` })}</span>
        <input
          value={answer}
          type={NUMBER_TOKENS.has(token) ? "number" : "text"}
          inputMode={NUMBER_TOKENS.has(token) ? "decimal" : undefined}
          min={NUMBER_TOKENS.has(token) ? "0" : undefined}
          onChange={(e) => setAnswer(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); fill(); } }}
          className="w-full rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1 text-base sm:text-xs"
        />
      </label>
      <button type="button" onClick={fill} disabled={!answer.trim()}
              className="self-end rounded border border-slate-400 dark:border-slate-500 px-2 py-1 disabled:opacity-50">
        {t("ui.reason_picker.blank_fill_btn", { defaultValue: "Fill in" })}
      </button>
    </div>
  );
};

export const ReasonPicker = ({
  value, onChange, onValidityChange, presets, minChars = 20,
  exampleAnswer, testid, placeholder,
  country, onDate, regime,
  onPresetPicked,
  tokenAutofill,
}) => {
  const { t } = useTranslation();
  const [pickedId, setPickedId] = useState(null);

  const handleChip = (preset) => {
    setPickedId(preset.id);
    if (preset.freeText) {
      onChange("");
    } else {
      // v0.5.0d+3 — `tokenAutofill` is a parent-supplied map of
      // `{ "<TOKEN>": "value" }` that gets substituted into the
      // template the instant a preset is picked. Used by callers
      // that already know the value the operator would type (eg
      // GroupedReviewQueue knows the flag's total amount and row
      // count from the API response). The fail-loud placeholder
      // validator stays in place — if a value is missing from the
      // autofill map, the raw `<TOKEN>` survives, the validator
      // catches it, and Save stays disabled. Auto-fill is a UX
      // sand-down, NOT a discipline-relax.
      let filled = preset.template || "";
      if (tokenAutofill) {
        for (const [tok, val] of Object.entries(tokenAutofill)) {
          if (val == null || String(val).trim() === "") continue;
          filled = filled.split(tok).join(String(val));
        }
      }
      onChange(filled);
    }
    // v0.5.0c+3 — parents can react to the preset pick itself
    // (e.g. TypedInEditor auto-fills value=0 when the no-sales
    // preset is picked). Fired AFTER onChange so the parent's
    // useEffect on `value` doesn't race.
    onPresetPicked?.(preset);
  };

  const v = computeValidity(value, minChars);
  const isValid = v.lengthOk && v.placeholdersOk;
  // Surface validity to parent every render — parent uses it on
  // the disabled prop of Save.
  React.useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  const counterTone = isValid
    ? "text-emerald-700 dark:text-emerald-300"
    : v.trimmedLen === 0
      ? "text-slate-500 dark:text-slate-400"
      : "text-amber-700 dark:text-amber-300";

  return (
    <div data-testid={testid} className="space-y-2">
      {/* v0.5.0c — vertical chip stack pivoted to a single
          dropdown (operator dogfood 2026-06-20: the chip column
          was too tall for the narrow Box-editor column). Same
          contract — picking an option fills the textarea with the
          preset's template; "Other (explain)" leaves the textarea
          empty. */}
      <select
        data-testid={`${testid}-select`}
        value={pickedId || ""}
        onChange={(e) => {
          const p = presets.find((x) => x.id === e.target.value);
          if (p) handleChip(p);
        }}
        className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-2 py-1.5 text-xs text-slate-900 dark:text-slate-50 cursor-pointer"
      >
        <option value="" disabled>
          {t("ui.reason_picker.pick_preset", { defaultValue: "Pick a preset reason…" })}
        </option>
        {presets.map((p) => (
          <option
            key={p.id}
            value={p.id}
            data-testid={`${testid}-option-${p.id}`}
          >
            {p.label}
          </option>
        ))}
      </select>
      {exampleAnswer && (
        <div className="text-[10px] text-slate-500 dark:text-slate-400 tabular-nums leading-snug">
          {/* i18n-ignore: wording pinned by test_v0499_typed_in_editor_ux */}Example: {exampleAnswer}
        </div>
      )}
      {/* v0.5.0c+2 — country-aware rate-token filler. Only renders
          when both `country` + `onDate` are provided AND the
          textarea currently contains `<RATE_DESC>` or `<RATE>`.
          Picking a rate substitutes the first occurrence so the
          operator never has to type the percentage by hand. */}
      <RateTokenFiller
        country={country}
        onDate={onDate}
        regime={regime}
        value={value}
        onChange={onChange}
        testid={`${testid}-rate-filler`}
      />
      {/* A2-571 (operator 2026-10-27: "We should not give options of free
          text only select dropdown") — no typed note: the preset IS the
          reason. What will be recorded is shown, read-only; any blank in
          it gets its own picker below. */}
      <p
        data-testid={`${testid}-reason`}
        data-filled={value ? "1" : "0"}
        className={`w-full rounded px-2 py-1 text-xs ${value
          ? "bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100"
          : "text-slate-500 dark:text-slate-400"}`}
      >
        {value || placeholder
          || t("ui.reason_picker.pick_first", { defaultValue: "Pick a reason above — it is recorded as the note." })}
      </p>
      {v.remaining.filter((t) => !RATE_TOKENS.has(t)).map((t) => {
        const props = { token: t, value, onChange, label: TOKEN_LABELS[t] || "the date",
                        testid: `${testid}-blank-${t.replace(/[<>]/g, "")}` };
        if (t === "<DATE>") return <DateBlankFiller key={t} {...props} />;
        if (t === "<DATE_RANGE>") return <DateRangeFiller key={t} {...props} />;
        if (CHOICES[t]) return <ChoiceBlankFiller key={t} {...props} />;
        return <BlankFiller key={t} {...props} />;
      })}
      <div
        data-testid={`${testid}-counter`}
        className={`text-xs tabular-nums ${counterTone}`}
      >
        {/* A2-571: no character count — nothing is typed; only what a
            picked reason still needs (a date, a choice) is said here. */}
        {v.lengthOk && !v.placeholdersOk && (
          <span
            data-testid={`${testid}-placeholder-error`}
            className="text-rose-700 dark:text-rose-300"
          >
            {/* v0.6.12 (A1-61) — say WHAT is still wanted, not which
                token is unfilled. The operator reported the old line as
                "some error, like replace placeholder token doc
                underscore ref": it rendered the literal <DOC_REF> and
                the words "placeholder token", which is engine
                vocabulary on screen. describePlaceholders maps each
                token to a plain-English name. */}
            {" "}{t("ui.reason_picker.still_to_fill", { defaultValue: "· still to fill in:" })}
            {" "}
            <span className="font-bold">
              {describePlaceholders(v.remaining).join(", ")}
            </span>
          </span>
        )}
      </div>
    </div>
  );
};

export default ReasonPicker;
